/**
 * Page réceptrice du handoff Office → Famla web
 * Charge la session, pré-remplit le générateur et propose un retour vers l'app hôte.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { useAppSelector } from '../../../stores';
import { Spiner } from '../../../components';
import { AppLayout, HeaderLayout } from '../../../layouts';
import { useHeaderLayoutCompanyData } from '../../../hooks/useHeaderLayoutCompanyData';
import { ProcessCreate } from '../../../features/process';
import {
    officeService,
    HandoffRetrieveData,
    ContextPayload,
} from '../../../services/api/officeService';
import {
    buildDesktopReturnUrls,
    buildHostWebReturnUrl,
    canUseDesktopProtocol,
    getHostAppLabel,
    openHostDesktopApp,
    openHostWebApp,
} from './microsoftDesktopLaunch';
import type { OfficeHostType } from './useOfficeContext';
import './OfficeHandoffLanding.css';

function buildThreadSummary(messages: import('../../../services/api/officeService').ChatMessage[]): string {
    if (!messages || messages.length === 0) return '';

    const lines = messages
        .filter((m) => m.role !== 'system')
        .map((m) => `${m.role === 'user' ? 'User' : 'Assistant'}: ${m.content}`);

    return lines.join('\n');
}

function buildPrefillFromContext(
    context: ContextPayload,
    threadMessages?: import('../../../services/api/officeService').ChatMessage[]
) {
    const title = context.subject
        || context.documentTitle
        || context.contextText
        || '';

    const descriptionParts = [
        context.emailBody,
        context.cellRange ? `Range: ${context.cellRange}` : null,
        context.contextText && context.contextText !== title ? context.contextText : null,
    ].filter(Boolean);

    let description = descriptionParts.join('\n\n');

    const threadSummary = buildThreadSummary(threadMessages ?? []);
    if (threadSummary) {
        description += `\n\nConversation context:\n${threadSummary}`;
    }

    const threadContext = threadSummary || undefined;

    return {
        name: title.slice(0, 120),
        description: description.slice(0, 4000),
        threadContext,
    };
}

const OfficeHandoffLanding: React.FC = () => {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const sessionToken = searchParams.get('session');
    const users = useAppSelector((state) => state.auth);
    const headerProps = useHeaderLayoutCompanyData();

    const [handoff, setHandoff] = useState<HandoffRetrieveData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const fetchStartedRef = useRef<string | null>(null);

    useEffect(() => {
        if (!sessionToken) {
            setError('missing');
            setLoading(false);
            return;
        }

        if (!users.access_token) {
            return;
        }

        if (fetchStartedRef.current === sessionToken) {
            return;
        }
        fetchStartedRef.current = sessionToken;

        const loadHandoff = async () => {
            setLoading(true);
            setError(null);

            try {
                const data = await officeService.retrieveHandoff(sessionToken, users.access_token);
                setHandoff(data);
            } catch (err) {
                setError(err instanceof Error ? err.message : 'unknown');
            } finally {
                setLoading(false);
            }
        };

        loadHandoff();
    }, [sessionToken, users.access_token]);

    const returnUrls = useMemo(() => {
        if (!handoff?.context) {
            return { protocolUri: null, webUrl: null };
        }

        const hostApp = (handoff.context.hostApp || 'Unknown') as OfficeHostType;
        const built = buildDesktopReturnUrls(hostApp, handoff.context.outlookItemId);

        return {
            protocolUri: built.protocolUri,
            webUrl: buildHostWebReturnUrl(hostApp, handoff.context.outlookItemId) || built.webUrl,
        };
    }, [handoff]);

    const hostLabel = handoff?.context?.hostApp
        ? getHostAppLabel(handoff.context.hostApp as OfficeHostType)
        : 'Office';

    const canOpenWeb = Boolean(returnUrls.webUrl);
    const canOpenDesktop = Boolean(returnUrls.protocolUri);
    const showReturnChoices = canOpenWeb || canOpenDesktop;

    const handleOpenWeb = () => {
        openHostWebApp(returnUrls.webUrl);
    };

    const handleOpenDesktop = () => {
        const isDesktopSupported = canUseDesktopProtocol();

        openHostDesktopApp(returnUrls.protocolUri, () => {
            toast(
                t(
                    'office.handoff.desktopTryAnyway',
                    '{{hostApp}} desktop may not be installed on this system. If nothing opens, use the browser option.',
                    { hostApp: hostLabel }
                ),
                { icon: 'ℹ️', duration: 5000 }
            );
        }, (handoff?.context?.hostApp || 'Unknown') as OfficeHostType);

        if (isDesktopSupported) {
            toast.success(
                t('office.handoff.desktopLaunching', 'Opening {{hostApp}} desktop...', { hostApp: hostLabel }),
                { icon: '🖥️' }
            );
        }
    };

    if (loading) {
        return (
            <AppLayout>
                <HeaderLayout {...headerProps} />
                <div className="office-handoff-landing office-handoff-landing--loading">
                    <Spiner className="d-flex justify-content-center spinner-border-sm" />
                </div>
            </AppLayout>
        );
    }

    if (error || !handoff) {
        return (
            <AppLayout>
                <HeaderLayout {...headerProps} />
                <div className="office-handoff-landing office-handoff-landing--error">
                    <h2>{t('office.handoff.errorTitle', 'Handoff session unavailable')}</h2>
                    <p>
                        {error === 'missing'
                            ? t('office.handoff.missingSession', 'No handoff session was provided.')
                            : error === 'auth'
                                ? t('office.handoff.authRequired', 'Please sign in to Famla to load this handoff session.')
                                : t('office.handoff.errorDesc', 'This session may have expired or already been used.')}
                    </p>
                    {error && error !== 'missing' && error !== 'auth' && (
                        <p className="office-handoff-landing__error-detail">{error}</p>
                    )}
                </div>
            </AppLayout>
        );
    }

    const prefill = buildPrefillFromContext(handoff.context, handoff.threadMessages);

    return (
        <AppLayout>
            <HeaderLayout {...headerProps} />
            <div className="office-handoff-landing">
                <div className="office-handoff-landing__banner">
                    <div>
                        <h2>{t('office.handoff.title', 'Create a process map from Office')}</h2>
                        <p>
                            {t(
                                'office.handoff.subtitle',
                                'Your {{hostApp}} context has been imported. Review and complete the form below.',
                                { hostApp: hostLabel }
                            )}
                        </p>
                    </div>
                    {showReturnChoices && (
                        <div className="office-handoff-landing__return-actions">
                            <span className="office-handoff-landing__return-label">
                                {t('office.handoff.returnChoose', 'Return to {{hostApp}}', { hostApp: hostLabel })}
                            </span>
                            <div className="office-handoff-landing__return-buttons">
                                {canOpenWeb && (
                                    <button
                                        type="button"
                                        className="office-handoff-landing__return-btn office-handoff-landing__return-btn--primary"
                                        onClick={handleOpenWeb}
                                    >
                                        {t('office.handoff.returnWeb', 'Open in browser')}
                                    </button>
                                )}
                                {canOpenDesktop && (
                                    <button
                                        type="button"
                                        className="office-handoff-landing__return-btn office-handoff-landing__return-btn--secondary"
                                        onClick={handleOpenDesktop}
                                        title={
                                            canUseDesktopProtocol()
                                                ? t('office.handoff.desktopHintSupported', 'Opens {{hostApp}} installed on your computer', { hostApp: hostLabel })
                                                : t('office.handoff.desktopHint', 'May require {{hostApp}} desktop (Windows/Mac)', { hostApp: hostLabel })
                                        }
                                    >
                                        {t('office.handoff.returnDesktop', 'Open desktop app')}
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                <div className="office-handoff-landing__form">
                    <ProcessCreate
                        handoffPrefill={prefill}
                        defaultType="HUMAN"
                    />
                </div>
            </div>
        </AppLayout>
    );
};

export default OfficeHandoffLanding;
